import { useQuery } from "@tanstack/react-query";
import { employeesAPI } from "../services/api";
import { useMemo } from "react";

interface UpcomingBirthday {
  employee: any;
  date: Date;
  daysUntil: number;
}

export const useUpcomingBirthdays = (
  daysAhead: number = 30,
  enabled: boolean = true,
) => {
  const { data: employees = [], isLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: employeesAPI.getAll,
    enabled,
  });

  const upcomingBirthdays = useMemo(() => {
    if (!employees || employees.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + daysAhead);

    const birthdays: UpcomingBirthday[] = [];

    employees.forEach((employee: any) => {
      if (!employee.birthDate || !employee.isActive) return;

      const birthDate = new Date(employee.birthDate);
      const currentYear = today.getFullYear();

      // Calculate birthday for this year
      const thisYearBirthday = new Date(
        currentYear,
        birthDate.getMonth(),
        birthDate.getDate(),
      );

      // If birthday already passed this year, use next year
      const nextBirthday =
        thisYearBirthday < today
          ? new Date(currentYear + 1, birthDate.getMonth(), birthDate.getDate())
          : thisYearBirthday;

      // Check if birthday is within the specified days ahead
      if (nextBirthday >= today && nextBirthday <= endDate) {
        const daysUntil = Math.ceil(
          (nextBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
        );
        birthdays.push({
          employee,
          date: nextBirthday,
          daysUntil,
        });
      }
    });

    // Sort by date (soonest first)
    return birthdays.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [employees, daysAhead]);

  return {
    upcomingBirthdays,
    isLoading,
  };
};
